import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
 CreditCardIcon,
 ShieldCheckIcon,
 ClockIcon,
 CheckCircleIcon,
 XMarkIcon,
 StarIcon,
 BoltIcon,
 AcademicCapIcon,
 UserGroupIcon,
 ChatBubbleLeftRightIcon,
 VideoCameraIcon,
 DocumentTextIcon,
 ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { processPayment } from '../services/razorpay';

const PaymentPage = () => {
 const navigate = useNavigate();
 const [paymentMethod, setPaymentMethod] = useState('card');
 const [isProcessing, setIsProcessing] = useState(false);
 const [error, setError] = useState(null);
 const [trialDaysLeft, setTrialDaysLeft] = useState(0);

 useEffect(() => {
 // Calculate trial days left for current user
 const userId = localStorage.getItem('user_id');
 if (userId) {
 const userCreatedDateKey = `user_created_date_${userId}`;
 const userCreatedDate = localStorage.getItem(userCreatedDateKey);
 if (userCreatedDate) {
 const createdDate = new Date(userCreatedDate);
 const now = new Date();
 const diffTime = Math.abs(now - createdDate);
 const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
 const daysLeft = Math.max(0, 7 - diffDays);
 setTrialDaysLeft(daysLeft);
 } else {
 // If no trial date set, user gets full 7 days
 setTrialDaysLeft(7);
 }
 }
 }, []);

 const handlePayment = async () => {
 setIsProcessing(true);
 setError(null);
 try {
 const amount = 2999;
 const userDetails = {
 name: localStorage.getItem('user_name') || 'User',
 email: localStorage.getItem('user_email') || 'user@example.com',
 phone: localStorage.getItem('user_phone') || '9999999999'
 };

 const result = await processPayment(amount, userDetails);
 
 if (result && result.success) {
 // Payment success - redirect will be handled by Razorpay
 console.log('Payment successful:', result);
 } else {
 // Payment failed
 const errorMessage = result?.error || 'Payment failed. Please try again.';
 setError(errorMessage);
 }
 } catch (error) {
 console.error('Payment failed:', error);
 setError(error.message || error || 'Payment failed. Please try again.');
 // Don't show alert, use visual error display instead
 } finally {
 setIsProcessing(false);
 }
 };

 const features = [
 {
 icon: <AcademicCapIcon className="h-6 w-6" />,
 title:"Full Course Access",
 description:"Access to all 50 phases of Software Engineering course"
 },
 {
 icon: <VideoCameraIcon className="h-6 w-6" />,
 title:"AI Video Tutorials",
 description:"Personalized AI-generated tutorial videos"
 },
 {
 icon: <ChatBubbleLeftRightIcon className="h-6 w-6" />,
 title:"Team Collaboration",
 description:"Real-time team chat and collaboration tools"
 },
 {
 icon: <UserGroupIcon className="h-6 w-6" />,
 title:"Mentor Support",
 description:"24/7 AI mentor assistance and feedback"
 },
 {
 icon: <DocumentTextIcon className="h-6 w-6" />,
 title:"Project Portfolio",
 description:"Build real-world projects and portfolio"
 },
 {
 icon: <ShieldCheckIcon className="h-6 w-6" />,
 title:"Certificate",
 description:"Industry-recognized completion certificate"
 }
 ];

 return (
 <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
 {/* Header */}
 <div className="bg-primary text-white py-4 px-6">
 <div className="max-w-7xl mx-auto flex items-center justify-between">
 <div className="flex items-center gap-3">
 <StarIcon className="h-8 w-8" />
 <h1 className="text-2xl font-bold">TheSkillGenie</h1>
 </div>
 <div className="flex items-center gap-4">
 <span className="text-sm font-medium">Premium Subscription</span>
 <button
 onClick={() => navigate('/dashboard')}
 className="bg-bgMain text-primary px-4 py-2 rounded-lg font-semibold hover:bg-bgSection transition-colors"
 >
 Back to Dashboard
 </button>
 </div>
 </div>
 </div>

 <div className="max-w-7xl mx-auto px-6 py-12">
 <div className="text-center mb-12">
 <h2 className="text-4xl font-bold text-white mb-4">
 Upgrade to Premium Access
 </h2>
 <p className="text-xl text-muted mb-6">
 {trialDaysLeft > 0 
 ? `Your free trial expires in ${trialDaysLeft} days`
 : 'Your free trial has expired'
 }
 </p>
 <div className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-full font-semibold">
 <BoltIcon className="h-5 w-5" />
 Get 90 days of unlimited access
 </div>
 </div>

 <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
 {/* Pricing Card */}
 <div className="bg-gradient-to-br from-primary to-secondary rounded-2xl p-8 text-white relative overflow-hidden">
 <div className="absolute top-0 right-0 bg-bgMain text-primary px-4 py-2 rounded-bl-2xl text-sm font-semibold">
 Most Popular
 </div>
 
 <div className="relative z-10">
 <h3 className="text-3xl font-bold mb-4">Premium Subscription</h3>
 <div className="mb-6">
 <span className="text-6xl font-bold">₹2,999</span>
 <span className="text-xl ml-2">for 90 days</span>
 </div>
 
 <div className="space-y-4 mb-8">
 <div className="flex items-center gap-3">
 <CheckCircleIcon className="h-5 w-5 text-white/80" />
 <span className="font-semibold">Full course access</span>
 </div>
 <div className="flex items-center gap-3">
 <CheckCircleIcon className="h-5 w-5 text-white/80" />
 <span className="font-semibold">AI-powered learning</span>
 </div>
 <div className="flex items-center gap-3">
 <CheckCircleIcon className="h-5 w-5 text-white/80" />
 <span className="font-semibold">Team collaboration</span>
 </div>
 <div className="flex items-center gap-3">
 <CheckCircleIcon className="h-5 w-5 text-white/80" />
 <span className="font-semibold">24/7 mentor support</span>
 </div>
 <div className="flex items-center gap-3">
 <CheckCircleIcon className="h-5 w-5 text-white/80" />
 <span className="font-semibold">Industry certificate</span>
 </div>
 </div>

 {error && (
 <div className="mb-4 p-4 bg-red-900/50 border border-red-500 rounded-lg">
 <div className="flex items-center gap-2">
 <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
 <p className="text-red-300 text-sm">{error}</p>
 </div>
 </div>
 )}

 <button
 onClick={handlePayment}
 disabled={isProcessing}
 className="w-full bg-bgMain text-primary py-4 rounded-xl font-bold text-lg hover:bg-bgSection disabled:opacity-50 transition-colors"
 >
 {isProcessing ? 'Processing...' : 'Subscribe Now'}
 </button>
 </div>
 </div>

 {/* Features & Payment */}
 <div className="space-y-8">
 {/* Features */}
 <div className="bg-bgSection rounded-2xl p-6 border border-gray-700">
 <h3 className="text-2xl font-bold text-white mb-6">What's Included</h3>
 <div className="grid grid-cols-1 gap-4">
 {features.map((feature, index) => (
 <div key={index} className="flex items-start gap-4 p-4 bg-bgSection rounded-lg">
 <div className="text-primary mt-1">
 {feature.icon}
 </div>
 <div>
 <h4 className="text-white font-semibold mb-1">{feature.title}</h4>
 <p className="text-muted text-sm">{feature.description}</p>
 </div>
 </div>
 ))}
 </div>
 </div>

 {/* Payment Methods */}
 <div className="bg-bgSection rounded-2xl p-6 border border-gray-700">
 <h3 className="text-2xl font-bold text-white mb-6">Payment Method</h3>
 
 <div className="space-y-4">
 <div 
 className={`flex items-center gap-4 p-4 bg-bgSection rounded-lg cursor-pointer transition-colors ${
 paymentMethod === 'card' ? 'border-2 border-primary' : 'border border-gray-600 hover:border-primary'
 }`}
 onClick={() => setPaymentMethod('card')}
 >
 <CreditCardIcon className="h-6 w-6 text-primary" />
 <div className="flex-1">
 <h4 className="text-white font-semibold">Credit/Debit Card</h4>
 <p className="text-muted text-sm">Visa, Mastercard, RuPay</p>
 </div>
 <div className={`w-4 h-4 rounded-full ${paymentMethod === 'card' ? 'bg-primary' : 'bg-gray-600'}`}></div>
 </div>

 <div 
 className={`flex items-center gap-4 p-4 bg-bgSection rounded-lg cursor-pointer transition-colors ${
 paymentMethod === 'upi' ? 'border-2 border-primary' : 'border border-gray-600 hover:border-primary'
 }`}
 onClick={() => setPaymentMethod('upi')}
 >
 <div className="w-6 h-6 bg-blue-600 rounded flex items-center justify-center">
 <span className="text-white text-xs font-bold">UPI</span>
 </div>
 <div className="flex-1">
 <h4 className="text-white font-semibold">UPI Payment</h4>
 <p className="text-muted text-sm">GPay, PhonePe, Paytm</p>
 </div>
 <div className={`w-4 h-4 rounded-full ${paymentMethod === 'upi' ? 'bg-primary' : 'bg-gray-600'}`}></div>
 </div>

 <div 
 className={`flex items-center gap-4 p-4 bg-bgSection rounded-lg cursor-pointer transition-colors ${
 paymentMethod === 'netbanking' ? 'border-2 border-primary' : 'border border-gray-600 hover:border-primary'
 }`}
 onClick={() => setPaymentMethod('netbanking')}
 >
 <div className="w-6 h-6 bg-green-600 rounded flex items-center justify-center">
 <span className="text-white text-xs font-bold">NB</span>
 </div>
 <div className="flex-1">
 <h4 className="text-white font-semibold">Net Banking</h4>
 <p className="text-muted text-sm">All major banks</p>
 </div>
 <div className={`w-4 h-4 rounded-full ${paymentMethod === 'netbanking' ? 'bg-primary' : 'bg-gray-600'}`}></div>
 </div>
 </div>
 </div>

 {/* Security */}
 <div className="bg-bgSection rounded-2xl p-6 border border-gray-700">
 <div className="flex items-center gap-3 mb-4">
 <ShieldCheckIcon className="h-6 w-6 text-green-400" />
 <h3 className="text-xl font-bold text-white">Secure Payment</h3>
 </div>
 <p className="text-muted text-sm">
 Your payment information is encrypted and secure. We use industry-standard SSL encryption 
 to protect your data. All transactions are processed securely.
 </p>
 </div>
 </div>
 </div>

 {/* Footer */}
 <div className="mt-16 text-center">
 <p className="text-muted text-sm">
 By subscribing, you agree to our Terms of Service and Privacy Policy
 </p>
 <div className="flex items-center justify-center gap-6 mt-4">
 <span className="text-muted text-sm">Need help?</span>
 <button className="text-primary hover:text-primary text-sm font-semibold">
 Contact Support
 </button>
 </div>
 </div>
 </div>
 </div>
 );
};

export default PaymentPage;



